import * as amplitude from '@amplitude/analytics-browser';
import { Identify } from '@amplitude/analytics-browser';

const AMPLITUDE_API_KEY = import.meta.env.VITE_AMPLITUDE_API_KEY;
let isInitialized = false;

const canTrack = () =>
  Boolean(AMPLITUDE_API_KEY) && localStorage.getItem('cookieConsent') === 'accepted';

const ensureInitialized = () => {
  if (isInitialized || !canTrack()) {
    return;
  }

  amplitude.init(AMPLITUDE_API_KEY ?? '', undefined, {
    autocapture: true,
    identityStorage: 'localStorage',
  });

  isInitialized = true;
};

export const trackEvent = (eventName: string, eventProperties?: Record<string, unknown>) => {
  if (!canTrack()) {
    return;
  }

  ensureInitialized();
  amplitude.track(eventName, eventProperties);
};

export interface UserProperties {
  role?: string;
  name?: string;
  email?: string;
  city?: string;
  province?: string;
  onboardingCompleted?: boolean;
  profileCompleted?: boolean;
  suitabilityTestStatus?: string;
  createdAt?: string;
}

export const identifyUser = (userId: string, userProperties?: UserProperties) => {
  if (!canTrack()) {
    return;
  }

  ensureInitialized();
  amplitude.setUserId(userId);

  if (userProperties) {
    const identify = new Identify();

    (
      Object.entries(userProperties) as [
        keyof UserProperties,
        UserProperties[keyof UserProperties],
      ][]
    ).forEach(([key, value]) => {
      if (value === undefined) {
        return;
      }

      switch (key) {
        case 'createdAt':
          if (value) {
            identify.setOnce('createdAt', value as string);
          }
          break;
        case 'onboardingCompleted':
        case 'profileCompleted':
          identify.set(key, value as boolean);
          break;
        case 'role':
        case 'name':
        case 'email':
        case 'city':
        case 'province':
        case 'suitabilityTestStatus':
          if (value) {
            identify.set(key, value as string);
          }
          break;
        default:
          break;
      }
    });
    amplitude.identify(identify);
  }
};

export const resetUser = () => {
  if (!canTrack()) {
    return;
  }

  ensureInitialized();
  amplitude.reset();
};
